import { FC, Key, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { SelectableListDataType, SelectableList } from '../../../../shared/selectable-list/selectable-list';
import { useQuery } from '@apollo/client';
import { ComponentQueryLoader } from '../../../../shared/component-query-loader';
import { ApplicantArchivedCaseListContainerArchivedCasesByApplicantIdDocument } from '../../../../../generated';
import { getItem } from '../../../../shared/local-storage';
import { formatCamelCase } from './case-list';

interface ArchivedCaseListContainerProps {}

export const ArchivedCaseListContainer: FC<ArchivedCaseListContainerProps> = () => {
  const navigate = useNavigate();
  const applicantUserId = getItem('applicantUserId');
  const location = useLocation();

  const [selectedArchivedCase, setSelectedArchivedCase] = useState<SelectableListDataType | undefined>();
  const { data, loading, error } = useQuery(ApplicantArchivedCaseListContainerArchivedCasesByApplicantIdDocument, {
    variables: {
      id: applicantUserId
    },
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    }
  });

  const formattedData = useMemo(() => {
    return data?.archivedCasesByApplicantId
      ?.map((item) => ({
        key: item?.id,
        initials: item?.caseName?.substring(0, 2).toUpperCase(),
        title: item?.caseName,
        timestamp: item?.archivedAt,
        progress: formatCamelCase(item?.state ?? ''),
        caseType: item?.caseType,
        schemaVersion: ''
      }))
      ?.sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1)) as SelectableListDataType[];
  }, [data]);

  const archivedCaseListRoutePath = useResolvedPath('');
  const selectedArchivedCaseRouteMatch = useMatch(`${archivedCaseListRoutePath.pathname}/:caseType/:caseId/archived`);

  useEffect(() => {
    setSelectedArchivedCase(undefined);

    if (selectedArchivedCaseRouteMatch && formattedData?.length > 0) {
      setSelectedArchivedCase(formattedData.find((r) => r?.key?.toString() === selectedArchivedCaseRouteMatch.params['caseId']));
      navigate(selectedArchivedCaseRouteMatch.pathname);
    }
  }, [formattedData, selectedArchivedCaseRouteMatch, location.pathname]);

  const onAnArchivedCaseSelected = (selectedRowKey: Key, caseType: string) => {
    setSelectedArchivedCase(formattedData.find((r) => r.key === selectedRowKey));
    navigate(`${caseType}/${selectedRowKey}/archived`);
  };

  return (
    <ComponentQueryLoader
      loading={loading}
      error={error}
      hasData={formattedData !== undefined}
      hasDataComponent={<SelectableList data={formattedData} selectedRecord={selectedArchivedCase} onSelectChange={onAnArchivedCaseSelected} />}
    />
  );
};
